import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  AggregatedStatus,
  BuildingInfo,
  DeviceRoles,
  DocumentType,
  FeaturePowerLimitationOverview,
  InvitationAccessLevel,
  PowerLimitationType,
  SubscriptionPlan,
} from '@vi-iot/client';

import {
  DashboardTypes,
  DevicesStore,
  Gateway,
  GatewayStatus,
  InstallationDetailsActions,
  InstallationDetailsSelectors,
  InstallationDevicePanels,
  InstallationPanelOrder,
  Platform,
  RichDevice,
} from '@store';

@Injectable({ providedIn: 'root' })
export class InstallationDetailsStore {
  getInstallationId$ = this.store.select(InstallationDetailsSelectors.getInstallationId());
  state$ = this.store.select(InstallationDetailsSelectors.getState());
  loading$ = this.store.select(InstallationDetailsSelectors.getLoadingIndicator());
  loaded$ = this.store.select(InstallationDetailsSelectors.getLoadedIndicator());
  device$ = this.store.select(InstallationDetailsSelectors.getDevice());
  address$ = this.store.select(InstallationDetailsSelectors.getAddress());
  owner$ = this.store.select(InstallationDetailsSelectors.getOwner());
  consumer$ = this.store.select(InstallationDetailsSelectors.getConsumerDetails());
  accessListUserList$ = this.store.select(InstallationDetailsSelectors.getAccessListUserList());
  accessListMaintainersList$ = this.store.select(
    InstallationDetailsSelectors.selectAccessListMaintainersList()
  );
  accessListEndUserAndFamilyMembersList$ = this.store.select(
    InstallationDetailsSelectors.selectAccessListOwnerAndFamilyMembersList()
  );

  ownershipType$ = this.store.select(InstallationDetailsSelectors.selectOwnershipType);

  accessListForCommercial$ = this.store.select(
    InstallationDetailsSelectors.getAccessListForCommercial()
  );
  installationGateways$ = this.store.select(InstallationDetailsSelectors.getInstallationGateways);
  isMultigatewayInstallation$ = this.store.select(
    InstallationDetailsSelectors.isMultigatewayInstallation
  );
  gatewaysSerial$ = this.store.select(InstallationDetailsSelectors.getGatewaysSerial());
  gatewaySerial$ = this.store.select(InstallationDetailsSelectors.getGatewaySerial());
  installationTimezone$ = this.store.select(InstallationDetailsSelectors.getInstallationTimezone());
  getSelectedGateway$ = this.store.select(InstallationDetailsSelectors.getSelectedGateway());
  getHemsGateway$ = this.store.select(InstallationDetailsSelectors.getHemsGateway());
  isSelectedGatewayACascade$ = this.store.select(
    InstallationDetailsSelectors.isSelectedGatewayACascade()
  );
  isSelectedGatewayAV2Cascade$ = this.store.select(
    InstallationDetailsSelectors.isSelectedGatewayAV2Cascade
  );
  isSelectedGatewayV1Cascade$ = this.store.select(
    InstallationDetailsSelectors.isSelectedGatewayV1Cascade()
  );
  isServiceLayerVisible$ = this.store.select(InstallationDetailsSelectors.isServiceLayerVisible());
  canEditBoilerSerial$ = this.store.select(
    InstallationDetailsSelectors.getBoilerSerialEditableFlag()
  );
  isTransferInstallationButtonVisible$ = this.store.select(
    InstallationDetailsSelectors.transferInstallationButtonVisible()
  );
  departmentName$ = this.store.select(InstallationDetailsSelectors.getDepartmentName());
  maintenancePackageLink$ = this.store.select(
    InstallationDetailsSelectors.getMaintenancePackageLink()
  );
  panelsOrder$ = this.store.select(InstallationDetailsSelectors.getPanelsOrder);
  panelsOrderLoaded$ = this.store.select(
    InstallationDetailsSelectors.getPanelsOrderLoadedIndicator()
  );
  availablePaidFeatures$ = this.store.select(
    InstallationDetailsSelectors.getAvailablePaidFeatures()
  );
  maintenanceChecklist$ = this.store.select(InstallationDetailsSelectors.getMaintenanceChecklist());
  isInstallationLegacyHP$ = this.store.select(
    InstallationDetailsSelectors.isInstallationLegacyHP()
  );
  serviceInstruction$ = this.store.select(InstallationDetailsSelectors.getServiceInstruction());
  isServiceInstructionLoading$ = this.store.select(
    InstallationDetailsSelectors.selectIsServiceInstructionLoading()
  );
  isInstallationVitocrossalNAType$ = this.store.select(
    InstallationDetailsSelectors.isInstallationVitocrossalNAType()
  );
  isMainDeviceVitocharge$ = this.store.select(
    InstallationDetailsSelectors.isMainDeviceVitocharge()
  );
  isMainDeviceB2C$ = this.store.select(InstallationDetailsSelectors.isMainDeviceB2C());
  getSequencedCascadeLagDevices$ = this.store.select(
    InstallationDetailsSelectors.getSequencedCascadeLagDevices()
  );
  hasInstallationDetailsLoaded$ = this.store.select(
    InstallationDetailsSelectors.isInstallationDetailsLoadingIndicator()
  );
  isPartnerShopLinkAvailable$ = this.store.select(
    InstallationDetailsSelectors.isPartnerShopLinkAvailable()
  );
  isMaintainerAssigned$ = this.store.select(InstallationDetailsSelectors.isMaintainerAssigned());

  isAnyConsumerInAccessList$ = this.store.select(
    InstallationDetailsSelectors.selectIsAnyConsumerInAccessList
  );
  isPlusOrProPackage$ = this.devicesStore.mainDeviceBoilerSerial$.pipe(
    switchMap((boilerSerial) =>
      this.store.select(InstallationDetailsSelectors.isPlusOrProPackage(boilerSerial))
    )
  );

  isNBIoT$ = this.store.select(InstallationDetailsSelectors.isNBIoT());
  getSelectedDashboardSlug$ = this.store.select(
    InstallationDetailsSelectors.getSelectedDashboardSlug()
  );
  getSelectedDashboardTypeHelper$ = this.store.select(
    InstallationDetailsSelectors.getSelectedDashboardTypeHelper()
  );

  getDeviceSerialForRemoteDiagnostics$ = this.store.select(
    InstallationDetailsSelectors.getDeviceSerialForRemoteDiagnostics()
  );

  installationBuildingInfo$ = this.store.select(
    InstallationDetailsSelectors.getInstallationBuildingInfo()
  );

  installationAddressInfo$ = this.store.select(
    InstallationDetailsSelectors.getInstallationAddressInfo
  );
  isFirmwareUpToDate$ = this.store.select(InstallationDetailsSelectors.isFirmwareUpToDate());

  isProPackageOrRsa$ = this.devicesStore.mainDeviceBoilerSerial$.pipe(
    switchMap((boilerSerial) =>
      this.store.select(InstallationDetailsSelectors.isProPackageOrRsa(boilerSerial))
    )
  );

  isMaintainerWithFreeOrBasicOrPlusPackage$ = this.devicesStore.mainDeviceBoilerSerial$.pipe(
    switchMap((boilerSerial) =>
      this.store.select(
        InstallationDetailsSelectors.isMaintainerWithFreeOrBasicOrPlusPackage(boilerSerial)
      )
    )
  );

  isPackageFetched$ = this.devicesStore.mainDeviceBoilerSerial$.pipe(
    switchMap((boilerSerial) =>
      this.store.select(InstallationDetailsSelectors.isPackageFetched(boilerSerial))
    )
  );
  vicarePlans$ = this.store.select(InstallationDetailsSelectors.getViCarePlans);
  vicarePlansForInstallation$ = this.store.select(
    InstallationDetailsSelectors.getViCarePlansFilteredByMonetizationFeatures
  );

  maintenanceChecklistStatus$ = this.store.select(
    InstallationDetailsSelectors.getMaintenanceChecklistStatus
  );

  isMainDeviceHeatPump$ = this.store.select(InstallationDetailsSelectors.isMainDeviceHeatPump);

  isMainDeviceBoiler$ = this.store.select(InstallationDetailsSelectors.isMainDeviceBoiler);

  trendingFeaturesOnDevices$ = this.store.select(
    InstallationDetailsSelectors.trendingFeaturesOnDevices()
  );

  isMenuBarVisible$ = this.store.select(InstallationDetailsSelectors.isMenuBarVisible());

  shouldFetchEnergyConsumptionDataForLegacyHeatpumps$ = this.store.select(
    InstallationDetailsSelectors.shouldFetchEnergyConsumptionDataForLegacyHeatpumps()
  );

  isPermamentInspectionFeatureSupported$ = this.store.select(
    InstallationDetailsSelectors.isPermamentInspectionFeatureSupported
  );
  isCancelMonitoringButtonVisible$ = this.store.select(
    InstallationDetailsSelectors.isCancelMonitoringButtonVisible()
  );
  deregisterGatewayState$ = this.store.select(
    InstallationDetailsSelectors.getDeregisterGatewayState()
  );
  isInstallationWithCommercialOwner$ = this.store.select(
    InstallationDetailsSelectors.selectInstallationWithCommercialOwner
  );
  isAccessListDefined$ = this.store.select(InstallationDetailsSelectors.selectIsAccessListDefined);
  isSubscriptionPlanLoading$ = this.store.select(
    InstallationDetailsSelectors.selectSubscriptionPlanLoading
  );

  installationType$ = this.store.select(InstallationDetailsSelectors.selectInstallationType);

  consumerAddress$ = this.store.select(InstallationDetailsSelectors.selectConsumerAddress);

  hemsAdvancedAnalysisStatus$ = this.store.select(
    InstallationDetailsSelectors.hemsAdvancedAnalysisStatus()
  );

  isServiceLayerButtonVisible$ = this.store.select(
    InstallationDetailsSelectors.selectIsServiceLayerButtonVisible()
  );

  isAccountsInAllCompanyEmpty$ = this.store.select(
    InstallationDetailsSelectors.selectIsAccountsInAllCompanyEmpty()
  );

  accessList$ = this.store.select(InstallationDetailsSelectors.selectAccessList());
  getAccessListUserByCustomer$ = this.store.select(
    InstallationDetailsSelectors.selectAccessListUserByCustomer()
  );
  constructor(private store: Store, private devicesStore: DevicesStore) {}

  getPanelsOrderForDevice(deviceId: string): Observable<InstallationPanelOrder[]> {
    return this.store.select(InstallationDetailsSelectors.getPanelsOrderForDevice(deviceId));
  }

  readInstallationDetails(installationId: number): void {
    this.store.dispatch(InstallationDetailsActions.ReadInstallationDetails({ installationId }));
  }

  selectDashboardTypeHelper(dashboardType: DashboardTypes): void {
    this.store.dispatch(InstallationDetailsActions.selectDashboardTypeHelper({ dashboardType }));
  }

  getInstallationName(
    ownerInstallationOfTranslation: string,
    noInstallationNameGenericTranslation: string
  ): Observable<string> {
    return this.store.select(
      InstallationDetailsSelectors.getDeviceDisplayName(
        ownerInstallationOfTranslation,
        noInstallationNameGenericTranslation
      )
    );
  }

  getGatewayStatus(gatewaySerial?: string): Observable<GatewayStatus> {
    return this.store.select(InstallationDetailsSelectors.getGatewayStatus(gatewaySerial));
  }

  isOTAOnGoing(gatewaySerial?: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.isOTAOnGoing(gatewaySerial));
  }

  getMainDevicePlatform(): Observable<Platform> {
    return this.store.select(InstallationDetailsSelectors.getMainDevicePlatform());
  }

  getMainDevicePlatformByGateway(gatewaySerial: string): Observable<Platform> {
    return this.store.select(
      InstallationDetailsSelectors.getMainDevicePlatformByGateway(gatewaySerial)
    );
  }

  liveUpdateAggregatedStatus(
    gatewaySerial: string,
    aggregatedStatus: AggregatedStatus,
    lastChangedAt: string
  ): void {
    this.store.dispatch(
      InstallationDetailsActions.LiveUpdateAggregatedStatus({
        gatewaySerial,
        aggregatedStatus,
        lastChangedAt,
      })
    );
  }

  stopMonitoring(ownerName: string): void {
    this.store.dispatch(InstallationDetailsActions.StopMonitoring({ ownerName }));
  }

  savePanelsOrder(deviceId: string, panelsOrder: InstallationPanelOrder[]): void {
    this.store.dispatch(InstallationDetailsActions.SavePanelsOrder({ deviceId, panelsOrder }));
  }

  savePanelsOrderMobile(panelsOrder: InstallationDevicePanels): void {
    this.store.dispatch(InstallationDetailsActions.SavePanelsOrderMobile({ panelsOrder }));
  }

  getPanelsOrder(gatewaySerial: string): void {
    this.store.dispatch(InstallationDetailsActions.GetPanelsOrder({ gatewaySerial }));
  }

  triggerMonetizationFeatures(installationId: number): void {
    this.store.dispatch(
      InstallationDetailsActions.TriggerGetMonetizationFeatures({ installationId })
    );
  }

  getMonetizationFeatures(installationId: number): void {
    this.store.dispatch(InstallationDetailsActions.GetMonetizationFeatures({ installationId }));
  }

  getMaintenanceChecklist(): void {
    this.store.dispatch(InstallationDetailsActions.GetMaintenanceChecklist());
  }

  getServiceInstruction(documentTypes: DocumentType[]): void {
    this.store.dispatch(InstallationDetailsActions.GetServiceInstruction({ documentTypes }));
  }

  hasSelectedDeviceGivenRoles(roles: DeviceRoles[]): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.hasSelectedDeviceGivenRoles(roles));
  }

  isBackupMenuItemVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.backupMenuItemVisible(gatewaySerial));
  }

  isSnapshotMenuItemVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.snapshotMenuItemVisible(gatewaySerial));
  }

  isExtendedParametersMenuItemVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.extendedParametersMenuItemVisible(gatewaySerial)
    );
  }
  isGtsMenuItemVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.isGuidedTroubleshootingAvailable(gatewaySerial)
    );
  }

  isTrendingMenuItemVisible(): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.trendingMenuItemVisible());
  }

  isDiagnosisMenuVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.diagnosisMenuVisible(gatewaySerial));
  }

  isDiagnosticsAvailable(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.isDiagnosticsAvailable(gatewaySerial));
  }

  isMaintenanceChecklistMenuItemVisible(): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.maintenanceChecklistMenuItemVisible());
  }

  isServiceInstructionMenuItemVisible(): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.serviceInstructionMenuItemVisible());
  }

  isHydraulicSchemeMenuItemVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.hydraulicSchemeMenuItemVisible(gatewaySerial)
    );
  }

  isPartnerAccessMenuVisible(): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.isPartnerAccessMenuVisible());
  }

  selectInstallationSubscriptionPlan(boilerSerial: string): Observable<SubscriptionPlan> {
    return this.store.select(
      InstallationDetailsSelectors.selectInstallationSubscriptionPlan(boilerSerial)
    );
  }

  isMainDeviceE3(gatewaySerial?: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.isMainDeviceE3(gatewaySerial));
  }

  showActiveErrorsForE3(gatewaySerial?: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.showActiveErrorsForE3(gatewaySerial));
  }

  getSpecificGatewayData(gatewaySerial): Observable<Gateway> {
    return this.store.select(InstallationDetailsSelectors.getSpecificGatewayData(gatewaySerial));
  }

  updateBuildingInfo(buildingInfo: BuildingInfo): void {
    this.store.dispatch(InstallationDetailsActions.updateBuildingInfo.attempt({ buildingInfo }));
  }

  deregisterGateway(installationId: number, gatewaySerial: string): void {
    this.store.dispatch(
      InstallationDetailsActions.deregisterGateway({ installationId, gatewaySerial })
    );
  }

  refreshDeviceList(installationId: number, gatewaySerial: string): void {
    this.store.dispatch(
      InstallationDetailsActions.refreshDeviceList({ installationId, gatewaySerial })
    );
  }

  resetDeregisterState(): void {
    this.store.dispatch(InstallationDetailsActions.resetDeregisterState());
  }

  getPermamentInspectionFeatureSupport(deviceSerial: string): void {
    this.store.dispatch(
      InstallationDetailsActions.getPermamentInspectionFeatureSupport({ deviceSerial })
    );
  }

  shouldDisplayBookedPackageCardBadgeForResidential(boilerSerial: string): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.shouldDisplayBookedPackageCardBadgeForResidential(boilerSerial)
    );
  }
  getAccessListCompanyName(invitationAccessLevel: InvitationAccessLevel): Observable<string> {
    return this.store.select(
      InstallationDetailsSelectors.getAccessListCompanyName(invitationAccessLevel)
    );
  }

  getMaintainerUsersList(customerNumber: number): void {
    this.store.dispatch(InstallationDetailsActions.getEmployeesList.attempt({ customerNumber }));
  }

  isEditBoilerSerialIconVisible(device: RichDevice): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.isEditBoilerSerialIconVisible(device));
  }

  selectGatewayHasDevices(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.selectGatewayHasDevices(gatewaySerial));
  }

  isPermanentInspectionMenuItemVisible(): Observable<boolean> {
    return this.store.select(InstallationDetailsSelectors.permanentInspectionMenuItemVisible());
  }

  isPermanentInspectionEntriesVisible(gatewaySerial: string): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.selectIsPermanentInspectionEntriesVisible(gatewaySerial)
    );
  }

  getFeaturePowerLimitationOverview(
    limitationType: PowerLimitationType
  ): Observable<FeaturePowerLimitationOverview> {
    return this.store.select(
      InstallationDetailsSelectors.getFeaturePowerLimitationOverview(limitationType)
    );
  }

  isPowerLimitationHistoryMenuItemVisible(device: RichDevice): Observable<boolean> {
    return this.store.select(
      InstallationDetailsSelectors.selectIsPowerLimitationHistoryLogMenuItemVisible(device)
    );
  }

  getAccountsByCompany(): void {
    this.store.dispatch(InstallationDetailsActions.getAccountsByCompany.attempt());
  }
}
